"use client";

import Link from "next/link";
import {
  ArrowRight,
  CalendarDays,
  MessageCircleQuestionMark,
  UsersRound,
} from "lucide-react";

import { useUpcomingEvents } from "@/hooks/use-events";
import { useHomeUnseenPostCounts } from "@/hooks/use-feed";
import { EMPTY_EVENT_FILTERS } from "@/lib/events/types";
import { cn, givenName } from "@/lib/utils";

type HomeGreetingProps = {
  fullName?: string;
  enabled: boolean;
};

export function HomeGreeting({ fullName, enabled }: HomeGreetingProps) {
  const eventsQuery = useUpcomingEvents(EMPTY_EVENT_FILTERS, enabled);
  const unseenQuery = useHomeUnseenPostCounts(enabled);
  const name = fullName ? givenName(fullName) : null;
  const questionCount = unseenQuery.data?.questions ?? 0;
  const communityCount = unseenQuery.data?.communityPosts ?? 0;
  const eventCount = (eventsQuery.data ?? []).length;

  return (
    <section className="hidden space-y-4 md:block">
      <div className="flex items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl font-bold tracking-[-0.025em] text-slate-900">
            {name ? `Üdvözöllek, ${name}! 👋` : "Üdvözöllek! 👋"}
          </h1>
          <p className="mt-1.5 text-sm leading-5 text-muted-foreground">
            Íme a legfontosabb frissítések az Ön közösségéből.
          </p>
        </div>
        <Link
          href="/activity"
          className="hidden shrink-0 items-center gap-1.5 text-sm font-semibold text-primary transition hover:text-primary/80 sm:inline-flex"
        >
          Összes frissítés
          <ArrowRight className="size-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <UpdateCard
          href="/feed?type=question"
          count={questionCount}
          title="Szakmai kérdés"
          subtitle="Új bejegyzés"
          icon={MessageCircleQuestionMark}
          iconClassName="bg-emerald-100/90 text-teal-600"
          washClassName="bg-emerald-50"
          arrowClassName="bg-emerald-50 text-teal-600"
        />
        <UpdateCard
          href="/feed?type=community"
          count={communityCount}
          title="Új közösségi bejegyzés"
          icon={UsersRound}
          iconClassName="bg-violet-100/90 text-violet-600"
          washClassName="bg-violet-50"
          arrowClassName="bg-violet-50 text-violet-600"
        />
        <UpdateCard
          href="/events"
          count={eventCount}
          title="Közelgő esemény"
          subtitle="a naptárban"
          icon={CalendarDays}
          iconClassName="bg-orange-100/90 text-orange-500"
          washClassName="bg-orange-50"
          arrowClassName="bg-orange-50 text-orange-500"
        />
      </div>
    </section>
  );
}

function UpdateCard({
  href,
  count,
  title,
  subtitle,
  icon: Icon,
  iconClassName,
  washClassName,
  arrowClassName,
}: {
  href: string;
  count: number;
  title: string;
  subtitle?: string;
  icon: typeof MessageCircleQuestionMark;
  iconClassName: string;
  washClassName: string;
  arrowClassName: string;
}) {
  return (
    <Link
      href={href}
      className="shadow-surface group relative min-h-[158px] min-w-0 overflow-hidden rounded-2xl border border-slate-200/80 bg-card p-5 transition-transform hover:-translate-y-0.5"
    >
      <div
        aria-hidden="true"
        className={cn("absolute inset-y-0 left-0 w-[18%] opacity-70", washClassName)}
      />
      <span
        aria-hidden="true"
        className={cn(
          "absolute -top-5 left-4 size-14 rounded-full opacity-50",
          washClassName,
        )}
      />
      <span
        aria-hidden="true"
        className={cn(
          "absolute top-8 left-8 size-10 rounded-full opacity-70",
          washClassName,
        )}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={cn(
            "flex size-12 shrink-0 items-center justify-center rounded-full",
            iconClassName,
          )}
        >
          <Icon className="size-6" />
        </div>
        <span className="text-3xl font-bold tabular-nums tracking-tight text-slate-950">
          {count}
        </span>
      </div>

      <div className="relative mt-4 pr-8">
        <p className="text-base font-bold leading-5 text-slate-900">{title}</p>
        {subtitle ? (
          <p className="mt-1 text-sm leading-5 text-muted-foreground">
            {subtitle}
          </p>
        ) : null}
      </div>

      <span
        className={cn(
          "absolute right-4 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-full transition-transform group-hover:translate-x-0.5",
          arrowClassName,
        )}
      >
        <ArrowRight className="size-5" />
      </span>
    </Link>
  );
}
